import React from 'react';
import { Button } from 'antd';
import { isRouteErrorResponse, useNavigate, useRouteError } from 'react-router-dom';
import { CircleAlert, MapPinOff } from 'lucide-react';
import { User } from '../../data';
import { homePathFor } from '../../core/access';
import { AuthBrandMark } from '../common/layout/AuthLayout';
import { EmptyState } from '../common/page';

/**
 * Fallback page for unknown URLs (`notFound`) and for errors thrown while rendering a route
 * (used as every route's errorElement) — replaces React Router's developer error screen.
 */
export default function RouteError({ notFound = false }) {
    const error = useRouteError();
    const navigate = useNavigate();
    const isNotFound = notFound || (isRouteErrorResponse(error) && error.status === 404);
    if (error && !isNotFound) console.error('Route render error:', error);

    // Before the session loads the user singleton is empty; '/' (login) forwards signed-in users home.
    const user = User.getInstance();
    const home = user ? homePathFor(user) : '/';

    return (
        <div className="flex min-h-screen flex-col items-center justify-center bg-surface-50 px-4">
            <div className="mb-6 flex items-center gap-2.5">
                <AuthBrandMark />
                <span className="text-base font-semibold tracking-tight text-surface-900">Shelf</span>
            </div>
            <div className="shelf-card w-full max-w-md">
                <EmptyState
                    icon={isNotFound ? <MapPinOff /> : <CircleAlert />}
                    tone={isNotFound ? 'neutral' : 'danger'}
                    title={isNotFound ? 'No encontramos esta página' : 'Algo salió mal'}
                    description={
                        isNotFound
                            ? 'La dirección no existe o fue movida. Revisa el enlace o vuelve al inicio.'
                            : 'Ocurrió un error inesperado al mostrar esta página. Intenta recargar; si continúa, avisa al equipo de soporte.'
                    }
                    action={
                        <div className="flex flex-wrap justify-center gap-2">
                            {!isNotFound && <Button onClick={() => window.location.reload()}>Recargar</Button>}
                            <Button type="primary" onClick={() => navigate(home, { replace: true })}>
                                Volver al inicio
                            </Button>
                        </div>
                    }
                />
            </div>
        </div>
    );
}
